/**
 * usePlaybookExtractionPubSub
 *
 * The live half of playbook generation. Connects to the `ai_processing` hub —
 * the same hub and the same wildcard-listener pattern as matter analysis
 * (`MatterForm`) and review-session extraction (`useReviewSessionPubSub`) —
 * joins the `playbook_{uuid}` group, and turns `PLAYBOOK_EXTRACTION_PROGRESS`
 * into the run state the progress panel reads.
 *
 * The engine decides its own step count and reports sub-progress per file, so
 * nothing here assumes a fixed pipeline; `playbookExtraction` owns the
 * normalizing and the state machine, and this hook owns only the socket
 * lifecycle. On completion the playbook detail is refetched so the clause and
 * global rule lists pick up what was just written.
 *
 * Redux (`REVIEW_EXTRACT_*`) is still dispatched alongside, because the create
 * form and the detail header read the extraction flags from the store.
 *
 * Lifecycle, mirroring `useResearchRun`:
 *
 *  • ONE `connectToHub('ai_processing')` per (uuid, user), retried with backoff
 *    — the call resolves FALSE rather than throwing, so an unchecked result
 *    left the panel spinning with no listener attached.
 *  • The user id is set on the service first; `negotiate()` refuses to make a
 *    request without one.
 *  • ONE wildcard listener, attached idempotently (off-then-on) BEFORE the join
 *    so nothing published in the gap is lost.
 *  • ONE join of `playbook_{uuid}`, guarded by a ref — the service re-joins its
 *    `activeGroup` itself on reconnect, so joining twice only duplicates
 *    delivery.
 *  • Teardown on unmount and on any uuid change: leave the group, remove the
 *    listener, cancel a pending retry.
 *
 * `ai_processing` is SHARED with matter analysis, drafting and research, so the
 * wildcard listener sees their traffic too; `normalizePlaybookEvent` is what
 * refuses it.
 *
 * Usage:
 *   const extraction = usePlaybookExtractionPubSub(playbookUuid, isExtracting);
 *   extraction.beginRun(files.map((f) => f.name));   // at submit
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector, type RootState } from '../../store';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import {
  fetchReviewDetail,
  REVIEW_EXTRACT_CONNECTING,
  REVIEW_EXTRACT_CONNECTED,
  REVIEW_EXTRACT_STEP,
  REVIEW_EXTRACT_COMPLETED,
  REVIEW_EXTRACT_FAILED,
  REVIEW_EXTRACT_RESET,
  REVIEW_EXTRACTING_PLAYBOOK,
} from '../../store/actions/Review/ReviewActions';
import {
  ExtractionRun,
  NormalizedCompletion,
  NormalizedFailure,
  beginExtractionRun,
  initialExtractionRun,
  normalizePlaybookEvent,
  playbookGroup,
  reduceExtractionRun,
} from './playbookExtraction';

const HUB = 'ai_processing';

/** How long to let the backend commit before re-reading the rules. */
const REFETCH_DELAY_MS = 1500;

/**
 * Connect retry schedule, same shape as the research run's.
 *
 * `connectToHub` RESOLVES FALSE on failure — it does not throw — so a dead
 * negotiate used to leave the panel spinning on "Connecting…" forever with no
 * listener attached and no way to find out.
 */
export const CONNECT_RETRY_MS = [500, 1500, 4000];

/**
 * How often to re-read `extraction_status` while a run is in flight.
 *
 * The socket is the fast path, not the only one. A dropped connection, a
 * filtered event or an engine that simply never publishes its last frame used
 * to leave this screen generating forever while the rules sat saved in the
 * database. The poll is what makes "stuck" impossible; it is silent, so it
 * never blinks the page it is polling behind.
 */
export const STATUS_POLL_MS = 8000;

/** Backend `extraction_status` values that mean the run is over. */
const TERMINAL_STATUSES = ['completed', 'complete', 'completed_with_errors', 'failed', 'error'];

export interface PlaybookExtractionController extends ExtractionRun {
  /** Seed the run with the files just submitted, before the socket is up. */
  beginRun: (fileNames: string[]) => void;
  /** Clear the panel — used when the user dismisses a finished run. */
  reset: () => void;
  /** Drop the failed-document notice without clearing the rest of the run. */
  dismissFailedDocuments: () => void;
}

export function usePlaybookExtractionPubSub(
  playbookUuid: string | undefined,
  enabled: boolean,
): PlaybookExtractionController {
  const dispatch = useAppDispatch();
  const groupRef = useRef<string | null>(null);
  const [run, setRun] = useState<ExtractionRun>(initialExtractionRun);

  /**
   * IDENTITY FIRST. `negotiate()` returns null immediately when the service has
   * no `currentUserId` — no request is even made, the socket never connects and
   * the engine's publish comes back `Not Found`. This hook used to inherit an
   * id only by accident, whenever notifications happened to connect first.
   */
  const userId = useAppSelector((s: RootState) => s.user?.userInfo?.UserID);

  /**
   * The backend's own verdict on this run, watched as a fallback for the
   * completion event. Read from the store rather than passed in, so both call
   * sites get the same guarantee and cannot drift apart.
   */
  const serverStatus = useAppSelector((s: RootState) =>
    s.review?.currentReview?.uuid === playbookUuid
      ? (s.review?.currentReview?.extraction_status ?? null)
      : null,
  );

  /** A run settles ONCE, whichever signal gets here first. */
  const settledRef = useRef(false);

  const beginRun = useCallback((fileNames: string[]) => {
    settledRef.current = false;
    setRun(beginExtractionRun(fileNames));
  }, []);

  const reset = useCallback(() => {
    settledRef.current = false;
    setRun(initialExtractionRun);
    dispatch({ type: REVIEW_EXTRACT_RESET });
  }, [dispatch]);

  const dismissFailedDocuments = useCallback(() => {
    setRun((prev) => (prev.failedDocuments.length ? { ...prev, failedDocuments: [] } : prev));
  }, []);

  const leaveGroup = useCallback(() => {
    if (!groupRef.current) return;
    getMultiHubSocketService().emit(HUB, 'leave', { group: groupRef.current });
    groupRef.current = null;
  }, []);

  /**
   * End the run, from either signal.
   *
   * Everything that has to happen when generation stops lives here and only
   * here: close the run state (which drives the panel out of its generating
   * view), clear the store's `isExtractingPlaybook` flag (which is what makes
   * the detail page swap the panel for the rules), leave the group, and
   * re-read the playbook so the rule lists show what was just written.
   */
  const settle = useCallback(
    (event: NormalizedCompletion | NormalizedFailure) => {
      setRun((prev) => reduceExtractionRun(prev, event));

      if (settledRef.current) return;
      settledRef.current = true;

      if (event.kind === 'completed') {
        dispatch({
          type: REVIEW_EXTRACT_COMPLETED,
          payload: { ok: true, summary: event.message || 'Extraction complete' },
        });
      } else {
        dispatch({ type: REVIEW_EXTRACT_FAILED, payload: { error: event.error } });
      }

      dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });
      leaveGroup();

      if (playbookUuid) {
        setTimeout(() => {
          dispatch(fetchReviewDetail(playbookUuid, { silent: true }) as any);
        }, REFETCH_DELAY_MS);
      }
    },
    [dispatch, leaveGroup, playbookUuid],
  );

  // ── Incoming events ──────────────────────────────────────────────────
  // The wildcard callback signature is (data, eventName) — the whole hub is
  // visible here, so the normalizer filters by playbook uuid.
  const handleMessage = useCallback(
    (raw: any, eventName?: string) => {
      const event = normalizePlaybookEvent(raw, eventName, playbookUuid);
      if (event.kind === 'ignore') return;

      if (event.kind === 'progress') {
        setRun((prev) => reduceExtractionRun(prev, event));
        if (event.stepId) {
          dispatch({
            type: REVIEW_EXTRACT_STEP,
            payload: {
              id: event.stepId,
              name: event.stepName || event.stepId,
              status: event.stepStatus,
              message: event.message || undefined,
            },
          });
        }
        return;
      }

      settle(event);
    },
    [dispatch, playbookUuid, settle],
  );

  // ── Socket lifecycle ─────────────────────────────────────────────────
  //
  // ONE connection to ONE hub, ONE wildcard listener, ONE join of
  // `playbook_{uuid}` — all torn down when the uuid changes or the component
  // unmounts. `attach` offs before it ons, so no path through this effect can
  // leave two handlers on the hub for one run.
  useEffect(() => {
    if (!enabled || !playbookUuid) return;

    const group = playbookGroup(playbookUuid);
    const service = getMultiHubSocketService();

    let cancelled = false;
    let attached: ((data: any, eventName?: string) => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    dispatch({ type: REVIEW_EXTRACT_CONNECTING });
    setRun((prev) => (prev.status === 'idle' ? { ...prev, status: 'connecting' } : prev));

    const attach = () => {
      // Idempotent by construction.
      if (attached) service.off(HUB, '*', attached);
      attached = handleMessage;
      service.on(HUB, '*', attached);
    };

    const join = () => {
      // The service records `activeGroup` and re-joins it on reconnect, so a
      // second explicit join would only duplicate delivery.
      if (groupRef.current === group) return;
      if (groupRef.current) service.emit(HUB, 'leave', { group: groupRef.current });
      service.emit(HUB, 'join', { group });
      groupRef.current = group;
    };

    const connect = async (attempt: number) => {
      try {
        if (userId) service.setCurrentUserId(String(userId));

        const connected = await service.connectToHub(HUB);
        if (cancelled) return;
        if (!connected) throw new Error('connect failed');

        // Listener BEFORE join: an event published between the two would
        // otherwise land on a hub nobody is listening to.
        attach();
        join();
        dispatch({ type: REVIEW_EXTRACT_CONNECTED });
      } catch (err) {
        if (cancelled) return;
        const delay = CONNECT_RETRY_MS[attempt];
        if (delay === undefined) {
          console.error('[usePlaybookExtractionPubSub] connect failed:', err);
          // Say so rather than spinning: generation continues server-side, so
          // the playbook is reachable even though this screen went blind.
          setRun((prev) =>
            prev.status === 'connecting' || prev.status === 'running'
              ? {
                  ...prev,
                  status: 'failed',
                  error:
                    'Lost connection to the generation service. Generation continues — reopen the playbook to check.',
                }
              : prev,
          );
          return;
        }
        retryTimer = setTimeout(() => connect(attempt + 1), delay);
      }
    };

    connect(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      leaveGroup();
      if (attached) service.off(HUB, '*', attached);
    };
  }, [enabled, playbookUuid, handleMessage, leaveGroup, dispatch, userId]);

  // ── Completion fallback: the backend's status ─────────────────────────
  //
  // The completion event is the fast path, not the only one. If it never
  // arrives — dropped socket, filtered event, an engine that stops publishing
  // after it writes the rules — the run is closed from `extraction_status`
  // instead, so the panel can never sit generating over a finished playbook.
  const isRunning = run.status === 'connecting' || run.status === 'running';

  useEffect(() => {
    if (!playbookUuid || !isRunning || !serverStatus) return;
    const status = String(serverStatus).toLowerCase();
    if (!TERMINAL_STATUSES.includes(status)) return;

    if (status === 'failed' || status === 'error') {
      settle({
        kind: 'failed',
        playbookUuid,
        error: 'Playbook generation failed',
        failedDocuments: [],
      });
      return;
    }

    settle({ kind: 'completed', playbookUuid, message: null, failedDocuments: [] });
  }, [playbookUuid, isRunning, serverStatus, settle]);

  // Poll for that status while the run is live. Silent — see `fetchReviewDetail`.
  useEffect(() => {
    if (!enabled || !playbookUuid || !isRunning) return;
    const timer = setInterval(() => {
      dispatch(fetchReviewDetail(playbookUuid, { silent: true }) as any);
    }, STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, playbookUuid, isRunning, dispatch]);

  // A new playbook is a new run: nothing about the last one carries over.
  useEffect(() => {
    settledRef.current = false;
  }, [playbookUuid]);

  // Redux progress is per-page scaffolding, not something to carry away.
  useEffect(
    () => () => {
      dispatch({ type: REVIEW_EXTRACT_RESET });
    },
    [dispatch],
  );

  return { ...run, beginRun, reset, dismissFailedDocuments };
}

export default usePlaybookExtractionPubSub;
